import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Users, ShieldCheck, Inbox, Loader2, MessageSquare, Crown, Trash2 } from 'lucide-react';
import { getAdminStats, getAdminUsers, getAdminContacts, toggleUserStatus, updateContactRequest, getAdminUserDetails, deleteUser, getAdminContactMessages, updateContactMessageStatus, deleteContactRequest, deleteContactMessage, cleanupOrphans } from '../lib/api';
import { AdminStatsGrid } from '../components/admin/AdminStatsGrid';
import { AdminUsersTable } from '../components/admin/AdminUsersTable';
import { AdminContactsList } from '../components/admin/AdminContactsList';
import { AdminContactMessages } from '../components/admin/AdminContactMessages';
import { AdminUserDetailModal } from '../components/admin/AdminUserDetailModal';

import { usePageTitle } from '../hooks/usePageTitle';

export default function Admin() {
  usePageTitle('Administration');
  const [tab, setTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, contactsRes, msgRes] = await Promise.all([
        getAdminStats(), getAdminUsers(), getAdminContacts(), getAdminContactMessages()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
      setContactMessages(msgRes.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Accès réservé aux administrateurs');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleStatus = async (userId, currentStatus, userName) => {
    try {
      await toggleUserStatus(userId);
      toast.success(`${userName} → ${currentStatus === 'abonne' ? 'Gratuit' : 'Pro'}`);
      loadData();
    } catch { toast.error('Erreur lors du changement de statut'); }
  };

  const handleContactStatus = async (requestId, newStatus) => {
    try {
      await updateContactRequest(requestId, newStatus);
      toast.success('Demande mise à jour');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const handleContactMessageStatus = async (requestId, newStatus) => {
    try {
      await updateContactMessageStatus(requestId, newStatus);
      toast.success('Statut mis à jour');
      setContactMessages(prev => prev.map(m => m.request_id === requestId ? { ...m, status: newStatus } : m));
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      toast.success('Compte supprimé');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  const handleDeleteContactRequest = async (requestId) => {
    try {
      await deleteContactRequest(requestId);
      toast.success('Demande supprimée');
      setContacts(prev => prev.filter(c => c.request_id !== requestId));
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleDeleteContactMessage = async (requestId) => {
    try {
      await deleteContactMessage(requestId);
      toast.success('Message supprimé');
      setContactMessages(prev => prev.filter(m => m.request_id !== requestId));
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleViewUser = async (userId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await getAdminUserDetails(userId);
      setUserDetail(res.data);
    } catch {
      toast.error('Erreur chargement détails');
      setDetailOpen(false);
    }
    setDetailLoading(false);
  };

  const handleCleanupOrphans = async () => {
    setCleaningUp(true);
    try {
      const res = await cleanupOrphans();
      const d = res.data;
      toast.success(`Nettoyage terminé : ${d.deleted_exams} examen(s), ${d.deleted_pdfs} PDF(s), ${d.deleted_results} résultat(s) orphelin(s) supprimé(s)`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du nettoyage');
    }
    setCleaningUp(false);
    setShowCleanupDialog(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingAbo = contacts.filter(c => c.status === 'pending').length;
  const pendingContact = contactMessages.filter(m => m.status === 'À contacter').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-3xl font-black tracking-tight">Administration</h1>
            <p className="text-muted-foreground text-sm">Panneau de contrôle super admin</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCleanupDialog(true)}
          disabled={cleaningUp}
          data-testid="cleanup-orphans-btn"
          className="text-destructive border-destructive/30 hover:bg-destructive/10"
        >
          {cleaningUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
          Nettoyer les orphelins
        </Button>
      </div>

      <AdminStatsGrid stats={stats} />

      <div className="flex gap-2 border-b border-border pb-1">
        <Button variant={tab === 'users' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('users')} className={tab === 'users' ? 'bg-primary text-primary-foreground' : ''} data-testid="admin-tab-users">
          <Users className="w-4 h-4 mr-1.5" /> Utilisateurs ({users.length})
        </Button>
        <Button variant={tab === 'contacts' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('contacts')} className={tab === 'contacts' ? 'bg-primary text-primary-foreground' : ''} data-testid="admin-tab-contacts">
          <Crown className="w-4 h-4 mr-1.5" /> Demande d'abo {pendingAbo > 0 && <span className="ml-1 bg-amber-500/20 text-amber-400 text-xs px-1.5 rounded-full">{pendingAbo}</span>}
        </Button>
        <Button variant={tab === 'messages' ? 'default' : 'ghost'} size="sm" onClick={() => setTab('messages')} className={tab === 'messages' ? 'bg-primary text-primary-foreground' : ''} data-testid="admin-tab-messages">
          <MessageSquare className="w-4 h-4 mr-1.5" /> Demande de contact {pendingContact > 0 && <span className="ml-1 bg-amber-500/20 text-amber-400 text-xs px-1.5 rounded-full">{pendingContact}</span>}
        </Button>
      </div>

      {tab === 'users' && (
        <AdminUsersTable users={users} onToggleStatus={handleToggleStatus} onViewUser={handleViewUser} onDeleteUser={handleDeleteUser} />
      )}
      {tab === 'contacts' && (
        <AdminContactsList contacts={contacts} onStatusChange={handleContactStatus} onDelete={handleDeleteContactRequest} />
      )}
      {tab === 'messages' && (
        <AdminContactMessages messages={contactMessages} onStatusChange={handleContactMessageStatus} onDelete={handleDeleteContactMessage} />
      )}

      <AdminUserDetailModal open={detailOpen} onOpenChange={setDetailOpen} loading={detailLoading} detail={userDetail} />

      <AlertDialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <AlertDialogContent className="bg-card border-border" data-testid="cleanup-orphans-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Nettoyer les données orphelines ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera tous les examens, PDFs et résultats dont le QCM parent n&apos;existe plus. Cette opération est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cleaningUp}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanupOrphans}
              disabled={cleaningUp}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-cleanup-btn"
            >
              {cleaningUp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Confirmer le nettoyage
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
